import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { MapPin, Loader2, Check, Home as HomeIcon, ArrowRight, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: '', phone: '', email: '', neighbourhood: '', city: '', notes: '' };

const NeighbourhoodInviteModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const close = (val) => {
    onOpenChange(val);
    if (!val) {
      setTimeout(() => { setForm(empty); setDone(false); }, 250);
    }
  };

  const canSubmit = form.name.trim() && form.phone.trim() && form.neighbourhood.trim() && form.city.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error('Please fill all required fields');
      return;
    }
    if (form.phone.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/neighbourhood-invite`, {
        ...form,
        email: form.email?.trim() || null,
        notes: form.notes?.trim() || null,
      });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="invite-modal" data-testid="invite-modal">
        <VisuallyHidden.Root>
          <DialogTitle>Invite Elderra to your neighbourhood</DialogTitle>
          <DialogDescription>Share your neighbourhood details and we'll reach out to organize a visit.</DialogDescription>
        </VisuallyHidden.Root>

        {done ? (
          <div className="invite-success" data-testid="invite-success">
            <div className="invite-success-icon"><Check size={36} strokeWidth={3} /></div>
            <h3>Got it. Thank you!</h3>
            <p>We'll reach out within 48 hours to plan a visit to <strong>{form.neighbourhood}</strong>, {form.city}.</p>
            <button className="invite-close-btn" onClick={() => close(false)} data-testid="invite-success-close">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="invite-form-wrap">
            <div className="invite-header">
              <div className="invite-icon-circle"><Sparkles size={22} /></div>
              <h3>Invite us to your neighbourhood</h3>
              <p>Got 5+ elders in your locality? We'll come run a free meetup, training, or DigiMitra session.</p>
            </div>

            <div className="invite-form">
              <div className="invite-row">
                <div className="invite-field">
                  <label>Your Name *</label>
                  <Input
                    data-testid="invite-name-input"
                    placeholder="e.g. Suresh Sharma"
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    autoFocus
                    disabled={loading}
                  />
                </div>
                <div className="invite-field">
                  <label>Phone *</label>
                  <Input
                    data-testid="invite-phone-input"
                    type="tel"
                    placeholder="98765 43210"
                    value={form.phone}
                    onChange={(e) => set('phone', e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="invite-row">
                <div className="invite-field">
                  <label><MapPin size={13} /> Neighbourhood / Society *</label>
                  <Input
                    data-testid="invite-neighbourhood-input"
                    placeholder="e.g. Greater Kailash, A Block"
                    value={form.neighbourhood}
                    onChange={(e) => set('neighbourhood', e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div className="invite-field">
                  <label>City *</label>
                  <Input
                    data-testid="invite-city-input"
                    placeholder="e.g. New Delhi"
                    value={form.city}
                    onChange={(e) => set('city', e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="invite-field">
                <label>Email <span className="invite-opt">(optional)</span></label>
                <Input
                  data-testid="invite-email-input"
                  type="email"
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="invite-field">
                <label>Anything else? <span className="invite-opt">(optional)</span></label>
                <Textarea
                  data-testid="invite-notes-input"
                  placeholder="How many elders, when works for you, what they need help with..."
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  rows={3}
                  disabled={loading}
                />
              </div>

              <Button
                type="submit"
                className="invite-submit-btn"
                disabled={loading || !canSubmit}
                data-testid="invite-submit-btn"
              >
                {loading
                  ? <><Loader2 size={16} className="adm-spin" /> Sending...</>
                  : <>Send Invite <ArrowRight size={16} /></>}
              </Button>
              <button type="button" className="invite-close-btn" onClick={() => close(false)} data-testid="invite-modal-cancel">
                Cancel
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NeighbourhoodInviteModal;
